import { ReactNode } from "react";
import { Icon } from "@iconify/react";
import { Colors } from "../../../utils/colors";
import RectangleButton from "../../Buttons/RectangleButton";
import { FoldHeaderText } from "./FoldHeaderText";

// Building blocks for the landing page folds that show a three-stage
// "source → Markit → destination" flow diagram.

const MONO_FONT = "'JetBrains Mono', monospace";

const pingDot = () => (
  <div style={{ position: "relative", width: 10, height: 10, flexShrink: 0 }}>
    <div
      style={{
        position: "absolute",
        inset: 0,
        borderRadius: "50%",
        backgroundColor: Colors.BLUE5,
        animation:
          "onboarding-dot-ping 1.8s cubic-bezier(0, 0, 0.2, 1) infinite",
      }}
    />
    <div
      style={{
        position: "relative",
        width: 10,
        height: 10,
        borderRadius: "50%",
        backgroundColor: Colors.BLUE5,
        boxShadow: "0 0 14px 4px rgba(0,122,255,0.55)",
      }}
    />
  </div>
);

export const wireConnector = (isDesktopSize: boolean) =>
  isDesktopSize ? (
    <div
      className="ColumnCentering"
      style={{ width: 40, alignSelf: "center", flexShrink: 0 }}
    >
      <div style={{ position: "relative", width: "100%", height: 1 }}>
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(0,122,255,0.35)",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          {pingDot()}
        </div>
      </div>
    </div>
  ) : (
    <div
      style={{
        position: "relative",
        height: 64,
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: 0,
          bottom: 0,
          width: 1,
          transform: "translateX(-50%)",
          backgroundColor: "rgba(0,122,255,0.35)",
        }}
      />
      {pingDot()}
    </div>
  );

export const stageLabel = (label: ReactNode, isDesktopSize: boolean) => (
  <span
    style={{
      color: Colors.BLUE5,
      fontSize: isDesktopSize ? 16 : 15,
      fontWeight: 600,
      letterSpacing: isDesktopSize ? 1.28 : 1.2,
      textAlign: isDesktopSize ? "left" : "center",
      width: isDesktopSize ? undefined : "100%",
    }}
  >
    {label}
  </span>
);

type FlowSourceRowProps = {
  icon: ReactNode;
  title: string;
  subtitle: string;
  time: string;
  highlighted?: boolean;
};

// A stage 1 row: what kicks off the flow (an activity on Markit, a prompt, etc.)
export const FlowSourceRow = (props: FlowSourceRowProps) => {
  const { icon, title, subtitle, time, highlighted } = props;
  return (
    <div
      className="AlignedRow"
      style={{
        gap: 12,
        padding: "12px 14px",
        borderRadius: 14,
        width: "100%",
        boxSizing: "border-box",
        opacity: highlighted ? 1 : 0.65,
        backgroundColor: highlighted ? "#0e1729" : "rgba(255,255,255,0.04)",
        border: highlighted ? "1px solid rgba(0,122,255,0.55)" : "none",
        boxShadow: highlighted ? "0 0 24px 0 rgba(0,122,255,0.25)" : "none",
      }}
    >
      <div
        className="ColumnCentering"
        style={{
          width: 38,
          height: 38,
          borderRadius: 19,
          backgroundColor: "rgba(255,255,255,0.07)",
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div className="ColumnNormal" style={{ gap: 4, flex: 1, minWidth: 0 }}>
        <span style={{ color: Colors.WHITE, fontSize: 15, fontWeight: 600 }}>
          {title}
        </span>
        <span
          style={{
            fontFamily: MONO_FONT,
            fontSize: 11,
            color: highlighted ? "#6ea8ff" : "#8c919e",
          }}
        >
          {subtitle}
        </span>
      </div>
      <span style={{ color: "#73737a", fontSize: 12, flexShrink: 0 }}>
        {time}
      </span>
    </div>
  );
};

type FlowResultRowProps = {
  logoBg: string;
  logoText: string;
  title: string;
  status: string;
};

// A stage 3 row: where the result lands, with a check once it's done
export const FlowResultRow = (props: FlowResultRowProps) => {
  const { logoBg, logoText, title, status } = props;
  return (
    <div
      className="AlignedRow"
      style={{
        gap: 12,
        padding: "11px 14px",
        borderRadius: 14,
        width: "100%",
        boxSizing: "border-box",
        backgroundColor: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.07)",
      }}
    >
      <div
        className="ColumnCentering"
        style={{
          width: 34,
          height: 34,
          borderRadius: 17,
          backgroundColor: logoBg,
          flexShrink: 0,
        }}
      >
        <span style={{ color: Colors.WHITE, fontSize: 15, fontWeight: 700 }}>
          {logoText}
        </span>
      </div>
      <div className="ColumnNormal" style={{ gap: 3, flex: 1, minWidth: 0 }}>
        <span style={{ color: Colors.WHITE, fontSize: 15, fontWeight: 600 }}>
          {title}
        </span>
        <span style={{ color: "#8c919e", fontSize: 12 }}>{status}</span>
      </div>
      <Icon icon="ion:checkmark" height={14} style={{ color: Colors.GREEN1 }} />
    </div>
  );
};

const windowDot = (color: string) => (
  <div
    style={{
      width: 8,
      height: 8,
      borderRadius: "50%",
      backgroundColor: color,
    }}
  />
);

type FlowCodeCardProps = {
  label: string;
  url: string;
  footerText: string;
  children: ReactNode;
};

// The stage 2 terminal-style card showing the payload Markit handles
export const FlowCodeCard = (props: FlowCodeCardProps) => {
  const { label, url, footerText, children } = props;
  return (
    <div
      style={{
        backgroundColor: "#090b0f",
        border: "1px solid rgba(255,255,255,0.09)",
        borderRadius: 16,
        boxShadow:
          "0 0 40px 0 rgba(0,122,255,0.12), 0 12px 40px 0 rgba(0,0,0,0.5)",
        width: "100%",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <div
        className="AlignedRow"
        style={{
          gap: 6,
          padding: "13px 16px",
          backgroundColor: "rgba(255,255,255,0.03)",
        }}
      >
        {windowDot("#ff5f56")}
        {windowDot("#ffbd2e")}
        {windowDot("#27c93f")}
        <div style={{ flex: 1 }} />
        <span
          style={{
            fontFamily: MONO_FONT,
            fontSize: 11,
            fontWeight: 600,
            color: "#4dd980",
          }}
        >
          {label}
        </span>
        <span style={{ fontFamily: MONO_FONT, fontSize: 11, color: "#8c919e" }}>
          {url}
        </span>
      </div>
      <div
        style={{
          padding: "16px 18px",
          fontFamily: MONO_FONT,
          fontSize: 13,
          lineHeight: "22px",
          color: "#858a99",
        }}
      >
        {children}
      </div>
      <div
        style={{
          height: 1,
          backgroundColor: "rgba(255,255,255,0.07)",
          width: "100%",
        }}
      />
      <div
        className="AlignedRow"
        style={{ gap: 7, padding: "11px 16px 12px 16px" }}
      >
        <span style={{ color: "#4dd980", fontSize: 12 }}>✓</span>
        <span style={{ color: "#8c919e", fontSize: 12, fontWeight: 500 }}>
          {footerText}
        </span>
      </div>
    </div>
  );
};

type FlowFoldIntroProps = {
  isDesktopSize: boolean;
  badgeIcon: ReactNode;
  badgeLabel: string;
  title: string;
  body: string;
  ctaLabel: string;
  ctaOnPress: () => void;
  chipPrefix: string;
  chipText: string;
};

// Badge, headline, body copy, CTA button and code chip above the flow diagram
export const FlowFoldIntro = (props: FlowFoldIntroProps) => {
  const {
    isDesktopSize,
    badgeIcon,
    badgeLabel,
    title,
    body,
    ctaLabel,
    ctaOnPress,
    chipPrefix,
    chipText,
  } = props;
  return (
    <div
      className="ColumnNormal"
      style={{ gap: 24, maxWidth: 640, alignItems: "flex-start" }}
    >
      <div className="ColumnNormal" style={{ gap: 10 }}>
        <div className="AlignedRow" style={{ gap: 7 }}>
          {badgeIcon}
          <span style={{ color: Colors.BLUE5, fontSize: 16, fontWeight: 500 }}>
            {badgeLabel}
          </span>
        </div>
        <FoldHeaderText
          isDesktopSize={isDesktopSize}
          textStyles={{ letterSpacing: "-1px" }}
        >
          {title}
        </FoldHeaderText>
      </div>
      <span
        style={{
          color: Colors.GRAY1,
          fontSize: 18,
          lineHeight: "27px",
          letterSpacing: "-0.5px",
        }}
      >
        {body}
      </span>
      <div
        className={isDesktopSize ? "AlignedRow" : "ColumnNormal"}
        style={{
          gap: isDesktopSize ? 16 : 12,
          width: isDesktopSize ? undefined : "100%",
        }}
      >
        <RectangleButton
          buttonLabel={ctaLabel}
          onPress={ctaOnPress}
          altColor={Colors.WHITE}
          altTextColor={Colors.BLACK}
          iconRight={
            <Icon
              icon="ion:chevron-right"
              height={12}
              style={{ color: Colors.BLACK }}
            />
          }
          containerStyles={{
            padding: "12px 16px",
            borderRadius: 10,
            width: "fit-content",
          }}
        />
        <div
          className="AlignedRow"
          style={{
            gap: 8,
            padding: "11px 14px",
            borderRadius: 10,
            backgroundColor: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.12)",
            width: "fit-content",
          }}
        >
          <span
            style={{ fontFamily: MONO_FONT, fontSize: 13, color: Colors.BLUE5 }}
          >
            {chipPrefix}
          </span>
          <span
            style={{
              fontFamily: MONO_FONT,
              fontSize: 13,
              color: "#a8adb8",
              whiteSpace: "nowrap",
            }}
          >
            {chipText}
          </span>
        </div>
      </div>
    </div>
  );
};
